import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ContactModal from "../components/ContactModal";
import { useLanguage } from "../contexts/LanguageContext";
import { 
  Shield, 
  ShieldCheck,
  Lock,
  Zap,
  Server,
  Users,
  ArrowRight,
  CheckCircle,
  Play,
  Globe,
  Cpu,
  Layers,
  MessageSquare,
  BarChart3,
  Headphones,
  ShoppingCart,
  GraduationCap,
  Wrench,
  Phone,
  ExternalLink,
  Building2,
  Award
} from "lucide-react";

const AIWizzPlatform = () => {
  const { currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactContext, setContactContext] = useState({ pageSource: '', buttonContext: '', buttonText: '' });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const openContactModal = (context, buttonText) => {
    setContactContext({ 
      pageSource: 'AIWizz Plattform', 
      buttonContext: context,
      buttonText: buttonText 
    });
    setIsContactModalOpen(true);
  };

  // Content based on language
  const content = {
    de: {
      seo: {
        title: 'AIWizz Plattform - Enterprise AI Solution | BROVKO',
        description: 'AIWizz ist die KI-Enterprise-Lösung für souveräne Unternehmen. 100% DSGVO-konform, Private Cloud, Made in Germany.',
        keywords: 'AIWizz, KI-Plattform, Enterprise AI, DSGVO, KI-Souveränität'
      },
      hero: {
        badge: 'Unsere Technologieplattform',
        title: 'AIWizz',
        subtitle: 'Die AI-Enterprise Solution von morgen',
        description: 'Startup-Geschwindigkeit. Enterprise-Sicherheit. Mit AIWizz setzen wir die Prinzipien der KI-Souveränität in die Praxis um.',
        cta1: 'Demo anfragen',
        cta2: 'Zur AIWizz Website'
      },
      connection: {
        title: 'KI-Souveränität trifft Enterprise-Plattform',
        subtitle: 'AIWizz ist die technische Umsetzung unserer KI-Souveränitäts-Prinzipien',
        description: 'Was BROVKO strategisch berät, setzt AIWizz technisch um. Die Plattform vereint alle Anforderungen an souveräne KI in einer zentralen Lösung.',
        linkText: 'Mehr zu KI-Souveränität erfahren'
      },
      principles: {
        title: 'Souveränitäts-Prinzipien in der Praxis',
        items: [
          {
            principle: 'DSGVO-Konformität',
            solution: 'Private Cloud, Daten bleiben in Deutschland',
            icon: Shield
          },
          {
            principle: 'Auditierbarkeit',
            solution: 'Lückenlose Protokollierung aller KI-Entscheidungen',
            icon: ShieldCheck
          },
          {
            principle: 'Erklärbarkeit',
            solution: 'Kontextbezogene Antworten mit Quellenangaben',
            icon: MessageSquare
          },
          {
            principle: 'Determinismus',
            solution: 'Rollenbasierte Steuerung mit definierten Zugriffsrechten',
            icon: Lock
          }
        ]
      },
      features: {
        title: 'Was AIWizz auszeichnet',
        items: [
          {
            title: 'Zentral orchestriert',
            description: 'KI-Systeme, Prozesse und Datenflüsse intelligent gesteuert',
            icon: Cpu
          },
          {
            title: '100% DSGVO-konform',
            description: 'Private Cloud mit höchsten Sicherheitsstandards',
            icon: Shield
          },
          {
            title: 'Sofort produktiv',
            description: 'Plug-in-Module für jeden Geschäftsbereich',
            icon: Zap
          },
          {
            title: 'Systemübergreifend',
            description: 'Nahtlose Integration mit CRM, ERP, CMS und mehr',
            icon: Layers
          }
        ]
      },
      modules: {
        title: 'AIWizz Module',
        subtitle: 'Sofort einsetzbare Plug-in-Module für jeden Geschäftsbereich',
        items: [
          {
            name: 'SalesWizz',
            description: 'Automatisierte Leadqualifizierung und intelligente Verkaufsprognosen',
            icon: BarChart3
          },
          {
            name: 'TrainWizz',
            description: 'KI-gestützte Mitarbeiterentwicklung und Schulungsprogramme',
            icon: GraduationCap
          },
          {
            name: 'ServiceWizz',
            description: 'Intelligente Ticketklassifizierung und proaktive Kundenkommunikation',
            icon: Headphones
          },
          {
            name: 'E-CommerceWizz',
            description: 'KI-optimierte Produktempfehlungen und dynamische Preisgestaltung',
            icon: ShoppingCart
          },
          {
            name: 'MaintenanceWizz',
            description: 'Prädiktive Wartung und intelligente Instandhaltungsplanung',
            icon: Wrench
          },
          {
            name: 'VoiceWizz',
            description: 'Sprachgesteuerte KI für ein- und ausgehende Telefonate',
            icon: Phone
          }
        ]
      },
      architecture: {
        title: 'Enterprise-Architektur',
        subtitle: '5-stufige Architektur für zukunftssichere KI-Lösungen',
        layers: [
          { level: 5, name: 'Multi-Agenten-Systeme', description: 'Orchestrierte Zusammenarbeit für komplexe Geschäftsprozesse' },
          { level: 4, name: 'KI-Agenten', description: 'Spezialisierte Assistenten für verschiedene Aufgaben' },
          { level: 3, name: 'Enterprise Content', description: 'Integration von Unternehmensdaten und Dokumenten' },
          { level: 2, name: 'System-Integration', description: 'Nahtlose Verbindung mit CRM, ERP, CMS, DWH' },
          { level: 1, name: 'Private LLM Foundation', description: 'Sichere Grundlage für Ihre KI-Anwendungen' }
        ]
      },
      stats: {
        items: [
          { value: '30+', label: 'Unternehmen vertrauen AIWizz' },
          { value: '100%', label: 'DSGVO-konform' },
          { value: '8 Wochen', label: 'Minimale Projektdauer' },
          { value: 'ISO 27001', label: 'Zertifiziert' }
        ]
      },
      cta: {
        title: 'Bereit für souveräne KI?',
        description: 'Lassen Sie uns gemeinsam besprechen, wie AIWizz Ihre KI-Transformation beschleunigen kann.',
        button1: 'Beratungsgespräch vereinbaren',
        button2: 'AIWizz Website besuchen'
      }
    },
    en: {
      seo: {
        title: 'AIWizz Platform - Enterprise AI Solution | BROVKO',
        description: 'AIWizz is the enterprise AI solution for sovereign companies. 100% GDPR compliant, Private Cloud, Made in Germany.',
        keywords: 'AIWizz, AI Platform, Enterprise AI, GDPR, AI Sovereignty'
      },
      hero: {
        badge: 'Our Technology Platform',
        title: 'AIWizz',
        subtitle: "Tomorrow's AI Enterprise Solution",
        description: 'Startup speed. Enterprise security. With AIWizz, we put the principles of AI sovereignty into practice.',
        cta1: 'Request Demo',
        cta2: 'Visit AIWizz Website'
      },
      connection: {
        title: 'AI Sovereignty Meets Enterprise Platform',
        subtitle: 'AIWizz is the technical implementation of our AI sovereignty principles',
        description: 'What BROVKO advises strategically, AIWizz implements technically. The platform combines all requirements for sovereign AI in one central solution.',
        linkText: 'Learn more about AI Sovereignty'
      },
      principles: {
        title: 'Sovereignty Principles in Practice',
        items: [
          {
            principle: 'GDPR Compliance',
            solution: 'Private Cloud, data stays in Germany',
            icon: Shield
          },
          {
            principle: 'Auditability',
            solution: 'Complete logging of all AI decisions',
            icon: ShieldCheck
          },
          {
            principle: 'Explainability',
            solution: 'Context-aware answers with source references',
            icon: MessageSquare
          },
          {
            principle: 'Determinism',
            solution: 'Role-based control with defined access rights',
            icon: Lock
          }
        ]
      },
      features: {
        title: 'What Makes AIWizz Special',
        items: [
          {
            title: 'Centrally Orchestrated',
            description: 'AI systems, processes and data flows intelligently controlled',
            icon: Cpu
          },
          {
            title: '100% GDPR Compliant',
            description: 'Private cloud with highest security standards',
            icon: Shield
          },
          {
            title: 'Instantly Productive',
            description: 'Plug-in modules for every business area',
            icon: Zap
          },
          {
            title: 'Cross-System',
            description: 'Seamless integration with CRM, ERP, CMS and more',
            icon: Layers
          }
        ]
      },
      modules: {
        title: 'AIWizz Modules',
        subtitle: 'Ready-to-use plug-in modules for every business area',
        items: [
          {
            name: 'SalesWizz',
            description: 'Automated lead qualification and intelligent sales forecasting',
            icon: BarChart3
          },
          {
            name: 'TrainWizz',
            description: 'AI-powered employee development and training programs',
            icon: GraduationCap
          },
          {
            name: 'ServiceWizz',
            description: 'Intelligent ticket classification and proactive customer communication',
            icon: Headphones
          },
          {
            name: 'E-CommerceWizz',
            description: 'AI-optimized product recommendations and dynamic pricing',
            icon: ShoppingCart
          },
          {
            name: 'MaintenanceWizz',
            description: 'Predictive maintenance and intelligent maintenance planning',
            icon: Wrench
          },
          {
            name: 'VoiceWizz',
            description: 'Voice-controlled AI for incoming and outgoing calls',
            icon: Phone
          }
        ]
      },
      architecture: {
        title: 'Enterprise Architecture',
        subtitle: '5-tier architecture for future-proof AI solutions',
        layers: [
          { level: 5, name: 'Multi-Agent Systems', description: 'Orchestrated collaboration for complex business processes' },
          { level: 4, name: 'AI Agents', description: 'Specialized assistants for different tasks' },
          { level: 3, name: 'Enterprise Content', description: 'Integration of company data and documents' },
          { level: 2, name: 'System Integration', description: 'Seamless connection with CRM, ERP, CMS, DWH' },
          { level: 1, name: 'Private LLM Foundation', description: 'Secure foundation for your AI applications' }
        ]
      },
      stats: {
        items: [
          { value: '30+', label: 'Companies trust AIWizz' },
          { value: '100%', label: 'GDPR compliant' },
          { value: '8 Weeks', label: 'Minimum project duration' },
          { value: 'ISO 27001', label: 'Certified' }
        ]
      },
      cta: {
        title: 'Ready for Sovereign AI?',
        description: "Let's discuss together how AIWizz can accelerate your AI transformation.",
        button1: 'Schedule Consultation',
        button2: 'Visit AIWizz Website'
      }
    }
  };

  const c = content[currentLanguage] || content.de;

  return (
    <div className="theme-brand bg-surface text-ink min-h-screen bg-surface" data-testid="aiwizz-platform-page">
      <SEOHead 
        title={c.seo.title}
        description={c.seo.description}
        keywords={c.seo.keywords}
        canonicalUrl="/aiwizz-plattform"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-10 bg-gradient-to-br from-slate-900 via-blue-900/20 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-[46rem]">
            <div className="inline-flex items-center gap-2 bg-teal-400/10 text-brand-400 px-3 py-1.5 rounded-full text-xs font-medium mb-4">
              <Cpu className="h-3 w-3" />
              {c.hero.badge}
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
              {c.hero.title}
            </h1>
            <p className="text-xl md:text-2xl text-brand-400 font-semibold mb-4">
              {c.hero.subtitle}
            </p>
            <p className="text-base text-ink-muted mb-6 max-w-[62ch]">
              {c.hero.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 text-sm font-semibold"
                onClick={() => openContactModal('Hero Section', c.hero.cta1)}
                data-testid="aiwizz-demo-btn"
              >
                <Play className="mr-2 h-4 w-4" />
                {c.hero.cta1}
              </Button>
              <a href="https://aiwizz.ai" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline" 
                  className="border-hairline-strong text-white hover:bg-white/[0.07] px-6 py-3 text-sm"
                >
                  {c.hero.cta2}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Connection to KI-Souveränität */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-teal-900/30 to-blue-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-surface-raised/50 backdrop-blur-sm rounded-xl p-6 border border-teal-500/20">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-brand to-blue-500 rounded-xl flex items-center justify-center">
                    <Shield className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-1">
                    {c.connection.title}
                  </h2>
                  <p className="text-sm text-brand-400 font-medium mb-2">
                    {c.connection.subtitle}
                  </p>
                  <p className="text-sm text-ink-muted">
                    {c.connection.description}
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <Link to="/souveraene-cloud">
                    <Button variant="outline" className="border-teal-500/50 text-brand-400 hover:bg-brand/10 text-sm">
                      {c.connection.linkText}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sovereignty Principles in Practice */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {c.principles.title}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {c.principles.items.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Card key={index} className="bg-surface-raised/50 border-hairline hover:border-teal-500/50 transition-colors">
                    <CardContent className="p-4 text-center">
                      <div className="bg-brand/10 p-3 rounded-lg w-fit mx-auto mb-3">
                        <IconComponent className="h-6 w-6 text-brand-400" />
                      </div>
                      <h3 className="text-base font-bold text-white mb-1">{item.principle}</h3>
                      <p className="text-xs text-ink-faint">{item.solution}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {c.stats.items.map((stat, index) => (
                <div key={index} className="text-center p-4 bg-surface-raised/50 rounded-lg border border-slate-700/30">
                  <div className="text-2xl font-bold text-brand-400 mb-1">{stat.value}</div>
                  <div className="text-xs text-ink-faint">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-6 text-center">
              {c.features.title}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {c.features.items.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div key={index} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/30">
                    <div className="bg-brand/10 p-2 rounded-lg w-fit mb-3">
                      <IconComponent className="h-5 w-5 text-brand-400" />
                    </div>
                    <h3 className="text-base font-bold text-white mb-1">{feature.title}</h3>
                    <p className="text-xs text-ink-faint">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="py-20 md:py-28 bg-slate-800/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-[64ch] mb-6">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-2">
                {c.modules.title}
              </h2>
              <p className="text-base text-ink-faint">
                {c.modules.subtitle}
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {c.modules.items.map((module, index) => {
                const IconComponent = module.icon;
                return (
                  <Card key={index} className="bg-surface-raised/50 border-hairline hover:border-blue-500/50 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="bg-blue-500/10 p-2 rounded-lg flex-shrink-0">
                          <IconComponent className="h-5 w-5 text-blue-400" />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-white mb-1">{module.name}</h3>
                          <p className="text-xs text-ink-faint">{module.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="max-w-[64ch] mb-6">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-2">
                {c.architecture.title}
              </h2>
              <p className="text-base text-ink-faint">
                {c.architecture.subtitle}
              </p>
            </div>
            <div className="space-y-2">
              {c.architecture.layers.map((layer, index) => (
                <div 
                  key={index} 
                  className="flex items-center gap-4 bg-surface-raised/50 rounded-lg p-3 border border-slate-700/30"
                  style={{ marginLeft: `${(5 - layer.level) * 20}px`, marginRight: `${(5 - layer.level) * 20}px` }}
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-brand to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {layer.level}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-bold text-white">{layer.name}</h3>
                    <p className="text-xs text-ink-faint">{layer.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-gradient-to-r from-teal-900/50 to-blue-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-white mb-3">
              {c.cta.title}
            </h2>
            <p className="text-base text-ink-muted mb-6">
              {c.cta.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                className="bg-brand hover:bg-brand-600 text-ink-onBrand px-6 py-3 text-sm font-semibold"
                onClick={() => openContactModal('CTA Section', c.cta.button1)}
              >
                {c.cta.button1}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <a href="https://aiwizz.ai" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline" 
                  className="border-hairline-strong text-white hover:bg-white/[0.07] px-6 py-3 text-sm"
                >
                  {c.cta.button2}
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      
      <ContactModal 
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        pageSource={contactContext.pageSource}
        buttonContext={contactContext.buttonContext}
        buttonText={contactContext.buttonText}
      />
    </div>
  );
};

export default AIWizzPlatform;
